import { useEffect, useState } from 'react';
import type { TaskBodyProps } from './index';
import { DesignPlanHistory } from './DesignPlanHistory';
import { RefinementDesignSidebar } from './RefinementDesignSidebar';
import { RequestClarificationAction } from './RequestClarificationAction';

const MAX_LEN = 10_000;
const PLACEHOLDER = 'Please paste here all refinement instructions given to the manufacturer';

export function SubmitRefinementDesignTask({
    workCase: c,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const [designPlan, setDesignPlan] = useState('');
    const trimmed = designPlan.trim();
    const valid = trimmed.length > 0 && trimmed.length <= MAX_LEN;

    useEffect(() => {
        onPayloadChange?.({
            payload: valid ? { designPlan: trimmed } : null,
            valid
        });
    }, [trimmed, valid, onPayloadChange]);

    const overLimit = trimmed.length > MAX_LEN;

    return (
        <div className="flex-1 min-h-0 flex flex-col px-4 pt-3 pb-3">
            <div className="flex-1 min-h-0 flex gap-3">
                {/* LEFT — refinement design instructions (top) + past submissions (bottom) */}
                <div className="flex-[6] min-w-0 flex flex-col gap-3">
                    <div className="flex-[3] min-h-0 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden font-inter">
                        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0 gap-3">
                            <span className="font-bold text-sm text-gray-800">
                                Refinement design instructions
                            </span>
                            <span
                                className={`text-[11px] tabular-nums ${overLimit ? 'text-red-500' : 'text-gray-400'}`}
                            >
                                {trimmed.length.toLocaleString()} / {MAX_LEN.toLocaleString()}
                            </span>
                        </div>
                        <div className="flex-1 min-h-0 px-5 py-4 flex flex-col">
                            <textarea
                                value={designPlan}
                                onChange={(e) => setDesignPlan(e.target.value)}
                                placeholder={PLACEHOLDER}
                                spellCheck
                                className={`flex-1 w-full px-3 py-2 border rounded-md text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none leading-relaxed ${
                                    overLimit
                                        ? 'border-red-300 focus:border-red-400'
                                        : trimmed.length > 0
                                          ? 'border-green-500 bg-green-50/40 focus:border-green-600'
                                          : 'border-gray-300 focus:border-[rgb(55,115,168)]'
                                }`}
                            />
                        </div>
                    </div>

                    {/* Past submissions — scoped to THIS refinement cycle via phaseEnteredAt so
              previous refinements' designs don't leak in. */}
                    <div className="flex-[2] min-h-0 flex flex-col">
                        <DesignPlanHistory
                            patientId={c.id}
                            title="Past submissions (this refinement)"
                            emptyText="No design submissions yet for this refinement."
                            submitType="submit-refinement-for-design"
                            preApprovedType="refinement-design-pre-approved"
                            since={c.phaseEnteredAt}
                        />
                    </div>
                </div>

                {/* RIGHT — patient context + refinement notes (all) + patient notes */}
                <div className="flex-[4] min-w-0 min-h-0 flex flex-col">
                    <RefinementDesignSidebar
                        patientId={c.id}
                        since={c.phaseEnteredAt}
                        revisionsHeaderAction={
                            <RequestClarificationAction
                                workCase={c}
                                onTaskComplete={onTaskComplete}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
}
